import { Button, Modal } from '@components/common';
import {
  FormInput,
  FormSelect,
  FormTextarea,
} from '@components/common/FormField';
import React, { useState } from 'react';
import {
  AccountRecord,
  PaymentMethodRecord,
  TaxRateRecord,
} from '../../../../types/accounting';
import {
  CUSTOM_EVENT_TYPE_VALUE,
  CUSTOM_REFERENCE_TYPE_VALUE,
  getPostingRuleEventDefinition,
  getPostingRuleEventOptions,
  getPostingRuleReferenceDefinition,
  getPostingRuleReferenceOptions,
  JOURNAL_SOURCE_MODULE_OPTIONS,
  POSTING_RULE_DIRECTION_OPTIONS,
  POSTING_RULE_STATUS_OPTIONS,
  PostingRuleFormState,
} from './postingRules';

interface FinancialPostingRuleFormModalProps {
  initial: PostingRuleFormState;
  title: string;
  accounts: AccountRecord[];
  taxRates: TaxRateRecord[];
  paymentMethods: PaymentMethodRecord[];
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (form: PostingRuleFormState) => void;
}

export function FinancialPostingRuleFormModal({
  initial,
  title,
  accounts,
  taxRates,
  paymentMethods,
  isSaving,
  onClose,
  onSubmit,
}: FinancialPostingRuleFormModalProps) {
  const [form, setForm] = useState<PostingRuleFormState>(initial);
  const eventOptions = getPostingRuleEventOptions(form.sourceModule);
  const referenceOptions = getPostingRuleReferenceOptions(
    form.sourceModule,
    form.eventType,
  );
  const selectedEventDefinition =
    form.eventType === CUSTOM_EVENT_TYPE_VALUE
      ? null
      : getPostingRuleEventDefinition(form.sourceModule, form.eventType);
  const selectedReferenceDefinition =
    !form.referenceType || form.referenceType === CUSTOM_REFERENCE_TYPE_VALUE
      ? null
      : getPostingRuleReferenceDefinition(
          form.sourceModule,
          form.referenceType,
        );

  const setField = <K extends keyof PostingRuleFormState>(
    key: K,
    value: PostingRuleFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const getNextReferenceType = (
    sourceModule: PostingRuleFormState['sourceModule'],
    eventType: string,
    currentReferenceType: string,
  ) => {
    const availableReferenceOptions = getPostingRuleReferenceOptions(
      sourceModule,
      eventType,
    );

    if (
      currentReferenceType === CUSTOM_REFERENCE_TYPE_VALUE ||
      availableReferenceOptions.some(
        (referenceDefinition) =>
          referenceDefinition.value === currentReferenceType,
      )
    ) {
      return currentReferenceType;
    }

    const eventDefinition =
      eventType === CUSTOM_EVENT_TYPE_VALUE
        ? null
        : getPostingRuleEventDefinition(sourceModule, eventType);

    if (
      eventDefinition?.defaultReferenceType &&
      availableReferenceOptions.some(
        (referenceDefinition) =>
          referenceDefinition.value === eventDefinition.defaultReferenceType,
      )
    ) {
      return eventDefinition.defaultReferenceType;
    }

    return availableReferenceOptions[0]?.value || '';
  };

  const applyEventDefaults = (eventType: string) => {
    const eventDefinition = getPostingRuleEventDefinition(
      form.sourceModule,
      eventType,
    );

    setForm((current) => ({
      ...current,
      metadataCategory:
        current.metadataCategory ||
        eventDefinition?.defaultMetadata?.category ||
        '',
      metadataScenario:
        current.metadataScenario ||
        eventDefinition?.defaultMetadata?.scenario ||
        '',
      metadataChannel:
        current.metadataChannel ||
        eventDefinition?.defaultMetadata?.channel ||
        '',
      referenceType: getNextReferenceType(
        current.sourceModule,
        eventType,
        current.referenceType,
      ),
      customReferenceType:
        getNextReferenceType(
          current.sourceModule,
          eventType,
          current.referenceType,
        ) === CUSTOM_REFERENCE_TYPE_VALUE
          ? current.customReferenceType
          : '',
      eventMetadataValues: Object.fromEntries(
        (eventDefinition?.metadataFields || []).map((field) => [
          field.key,
          current.eventMetadataValues[field.key] || '',
        ]),
      ),
    }));
  };

  const handleSourceModuleChange = (
    sourceModule: PostingRuleFormState['sourceModule'],
  ) => {
    const nextEventOptions = getPostingRuleEventOptions(sourceModule);
    const hasCurrentEvent = nextEventOptions.some(
      (eventDefinition) => eventDefinition.value === form.eventType,
    );
    const nextEventType = hasCurrentEvent
      ? form.eventType
      : nextEventOptions[0]?.value || CUSTOM_EVENT_TYPE_VALUE;
    const nextReferenceType = getNextReferenceType(
      sourceModule,
      nextEventType,
      form.referenceType,
    );

    setForm((current) => ({
      ...current,
      sourceModule,
      eventType: nextEventType,
      customEventType:
        nextEventType === CUSTOM_EVENT_TYPE_VALUE
          ? current.customEventType
          : '',
      referenceType: nextReferenceType,
      customReferenceType:
        nextReferenceType === CUSTOM_REFERENCE_TYPE_VALUE
          ? current.customReferenceType
          : '',
    }));

    if (nextEventType !== CUSTOM_EVENT_TYPE_VALUE) {
      const eventDefinition = getPostingRuleEventDefinition(
        sourceModule,
        nextEventType,
      );

      if (eventDefinition?.defaultMetadata) {
        setForm((current) => ({
          ...current,
          metadataCategory:
            current.metadataCategory ||
            eventDefinition.defaultMetadata?.category ||
            '',
          metadataScenario:
            current.metadataScenario ||
            eventDefinition.defaultMetadata?.scenario ||
            '',
          metadataChannel:
            current.metadataChannel ||
            eventDefinition.defaultMetadata?.channel ||
            '',
        }));
      }
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Rule name"
            required
            value={form.ruleName}
            onChange={(event) => setField('ruleName', event.target.value)}
            placeholder="e.g. Sales cash collection"
          />
          <FormSelect
            label="Event type"
            required
            value={form.eventType}
            onChange={(event) => {
              const nextEventType = event.target.value;
              setField('eventType', nextEventType);

              if (nextEventType !== CUSTOM_EVENT_TYPE_VALUE) {
                setField('customEventType', '');
              }
              applyEventDefaults(nextEventType);
            }}
          >
            {eventOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            <option value={CUSTOM_EVENT_TYPE_VALUE}>Custom event</option>
          </FormSelect>
        </div>

        {selectedEventDefinition ? (
          <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {selectedEventDefinition.description}
          </div>
        ) : null}

        {form.eventType === CUSTOM_EVENT_TYPE_VALUE ? (
          <FormInput
            label="Custom event type"
            required
            value={form.customEventType}
            onChange={(event) =>
              setField('customEventType', event.target.value)
            }
            placeholder="e.g. custom_settlement_adjustment"
          />
        ) : null}

        <div className="grid gap-4 md:grid-cols-3">
          <FormSelect
            label="Source module"
            required
            value={form.sourceModule}
            onChange={(event) =>
              handleSourceModuleChange(
                event.target.value as PostingRuleFormState['sourceModule'],
              )
            }
          >
            {JOURNAL_SOURCE_MODULE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Direction"
            required
            value={form.direction}
            onChange={(event) =>
              setField(
                'direction',
                event.target.value as PostingRuleFormState['direction'],
              )
            }
          >
            {POSTING_RULE_DIRECTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormInput
            label="Priority"
            type="number"
            min="0"
            required
            value={form.priority}
            onChange={(event) => setField('priority', event.target.value)}
            placeholder="100"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormSelect
            label="Reference type"
            value={form.referenceType}
            onChange={(event) => {
              const nextReferenceType = event.target.value;
              setField('referenceType', nextReferenceType);

              if (nextReferenceType !== CUSTOM_REFERENCE_TYPE_VALUE) {
                setField('customReferenceType', '');
              }
            }}
          >
            <option value="">No reference scope</option>
            {referenceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            <option value={CUSTOM_REFERENCE_TYPE_VALUE}>
              Custom reference
            </option>
          </FormSelect>
          <FormSelect
            label="Status"
            required
            value={form.status}
            onChange={(event) =>
              setField(
                'status',
                event.target.value as PostingRuleFormState['status'],
              )
            }
          >
            {POSTING_RULE_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </div>

        {selectedReferenceDefinition ? (
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {selectedReferenceDefinition.description}
          </div>
        ) : null}

        {form.referenceType === CUSTOM_REFERENCE_TYPE_VALUE ? (
          <FormInput
            label="Custom reference type"
            required
            value={form.customReferenceType}
            onChange={(event) =>
              setField('customReferenceType', event.target.value)
            }
            placeholder="e.g. custom_refund_batch"
          />
        ) : null}

        {selectedEventDefinition?.metadataFields?.length ? (
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Event-specific metadata
              </h3>
              <p className="mt-1 text-xs text-gray-600">
                These fields help keep rules consistent for this event type.
              </p>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {selectedEventDefinition.metadataFields.map((field) => {
                const value = form.eventMetadataValues[field.key] || '';

                if (field.type === 'select') {
                  return (
                    <FormSelect
                      key={field.key}
                      label={field.label}
                      value={value}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          eventMetadataValues: {
                            ...current.eventMetadataValues,
                            [field.key]: event.target.value,
                          },
                        }))
                      }
                    >
                      <option value="">
                        Select {field.label.toLowerCase()}
                      </option>
                      {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                  );
                }

                return (
                  <FormInput
                    key={field.key}
                    label={field.label}
                    value={value}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        eventMetadataValues: {
                          ...current.eventMetadataValues,
                          [field.key]: event.target.value,
                        },
                      }))
                    }
                    placeholder={field.placeholder}
                  />
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-3">
          <FormSelect
            label="Account"
            required
            value={form.accountId}
            onChange={(event) => setField('accountId', event.target.value)}
          >
            <option value="">Select account</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} - {account.name}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Tax rate"
            value={form.taxRateId}
            onChange={(event) => setField('taxRateId', event.target.value)}
          >
            <option value="">No tax rate</option>
            {taxRates.map((taxRate) => (
              <option key={taxRate.id} value={taxRate.id}>
                {taxRate.code} - {taxRate.name}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Payment method"
            value={form.paymentMethodId}
            onChange={(event) =>
              setField('paymentMethodId', event.target.value)
            }
          >
            <option value="">No payment method</option>
            {paymentMethods.map((method) => (
              <option key={method.id} value={method.id}>
                {method.name}
              </option>
            ))}
          </FormSelect>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Structured metadata
            </h3>
            <p className="mt-1 text-xs text-gray-600">
              Use guided fields for common filters first. Advanced JSON is only
              needed for uncommon cases.
            </p>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <FormInput
              label="Category"
              value={form.metadataCategory}
              onChange={(event) =>
                setField('metadataCategory', event.target.value)
              }
              placeholder="e.g. refund"
            />
            <FormInput
              label="Scenario"
              value={form.metadataScenario}
              onChange={(event) =>
                setField('metadataScenario', event.target.value)
              }
              placeholder="e.g. sales-order-refund"
            />
            <FormInput
              label="Channel"
              value={form.metadataChannel}
              onChange={(event) =>
                setField('metadataChannel', event.target.value)
              }
              placeholder="e.g. pos"
            />
          </div>
        </div>

        <FormTextarea
          label="Advanced metadata (JSON)"
          value={form.metadataJson}
          onChange={(event) => setField('metadataJson', event.target.value)}
          rows={4}
          placeholder='Optional extra JSON, e.g. {"posting_group":"cash-refunds"}'
        />

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save rule
          </Button>
        </div>
      </form>
    </Modal>
  );
}
